import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  Button,
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
  DataTable,
  DataTablePagination,
  DataTableToolbar,
  Input,
  SubmitButton,
  TableBody,
  toast,
  useSWRDataTable,
  useTableContext,
} from "@bleu.builders/ui";
import { PlusIcon } from "@radix-ui/react-icons";
import { serialize } from "object-to-formdata";
import React from "react";
import { useParams } from "react-router";
import useSWRMutation from "swr/mutation";

import { Link } from "#/components/Link";
import { fetcher } from "#/lib/fetcher";
import { AdminPagePath } from "#/lib/pagesHelper";
import { useSWRDataTableWithRedirect } from "#/utils/useSWRDataTableWithRedirect";

function AddNewBillingPlan() {
  return (
    <Link to={AdminPagePath.BILLING_PLANS_NEW}>
      <Button size="sm" className="ml-auto hidden h-8 lg:flex text-sm">
        <PlusIcon className="mr-2 size-4" />
        Criar novo plano
      </Button>
    </Link>
  );
}

const SelectPlan = ({ billing_plan_id, couponCode }) => {
  const cancelRef = React.useRef<HTMLButtonElement>();
  const { organization_id } = useParams();

  const { trigger, isMutating } = useSWRMutation(
    `/admin/checkout_links`,
    fetcher,
  );

  const onSelectPlan = async () => {
    const formData = serialize({
      billing_plan_id,
      coupon_code: couponCode,
      organization_id,
    });

    const result = await trigger({ formData, method: "POST" });

    if (!result.success) {
      toast({
        title: "Erro!",
        description: result.errors,
        variant: "destructive",
      });
    }

    if (result.success) {
      toast({
        title: "Sucesso!",
        description:
          "Link de pagamento criado com sucesso, basta usuário clicar no banner para se inscrever.",
      });
    }

    cancelRef.current.click();
  };

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button className="w-1/2">Selecionar Plano</Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Voce tem certeza?</AlertDialogTitle>
          <AlertDialogDescription>
            Uma vez alterada a assinatura, o usuário terá que se inscrever de
            novo.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel ref={cancelRef}>Sair</AlertDialogCancel>
          <SubmitButton
            variant="destructive"
            type="button"
            onClick={onSelectPlan}
            isSubmitting={isMutating}
          >
            Confirmar alteração de plano
          </SubmitButton>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

function PlanItem({ row }) {
  const [coupon, setCoupon] = React.useState("");
  const getRowItem = (row, name) => {
    return row
      .getVisibleCells()
      .find((cell) => cell.column.id === name)
      ?.getValue();
  };

  return (
    <Card className="w-72 bg-background max-w-none flex flex-col border justify-between ">
      <CardHeader className="flex flex-col items-center space-y-1 text-center">
        <CardTitle className="text-xl pl-0">
          {getRowItem(row, "name")}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 text-sm w-full">
          <div>Valor</div>
          <div className="font-semibold text-right">
            {getRowItem(row, "price_cents")}
          </div>
          <div>Gateway de pagamento</div>
          <div className="text-right">{getRowItem(row, "processor")}</div>
          <div>Intervalo</div>
          <div className="text-right">{getRowItem(row, "interval")}</div>
        </div>
      </CardContent>
      <CardFooter className="flex gap-1 justify-between">
        <Input
          value={coupon}
          onChange={(e) => setCoupon(e.target.value)}
          placeholder="Cupom"
          className="w-1/2"
        />
        <SelectPlan
          billing_plan_id={getRowItem(row, "id")}
          couponCode={coupon}
        />
      </CardFooter>
    </Card>
  );
}

function TableItems() {
  // @ts-expect-error TS(2339) FIXME: Property 'table' does not exist on type '{}'.
  const { table } = useTableContext();

  return (
    <>
      <div className="flex gap-4 my-2 flex-wrap ">
        {table.getRowModel().rows?.length ? (
          table
            .getRowModel()
            .rows.map((row) => <PlanItem key={row.id} row={row} />)
        ) : (
          <div className="mt-4 w-full mr-4 overflow-y-auto max-h-[80vh] min-h-[10vh]">
            Nenhum resultado encontrado.
          </div>
        )}
      </div>
    </>
  );
}

export function PlansTable({ plans_type = "subscription" }) {
  const { data, error, isLoading, tableState, setTableState } =
    useSWRDataTableWithRedirect(
      AdminPagePath.BILLABLE_BILLING_PLANS + `?plans_type=${plans_type}`,
    );

  if (isLoading) {
    <div className="mt-4 w-full mr-4 overflow-y-auto max-h-[80vh] min-h-[10vh]">
      Carregando...
    </div>;
  }

  return (
    <div className="m-2 space-y-4 w-full">
      <span className="border-b-2 border-primary text-lg font-bold">
        Planos
      </span>
      <DataTable
        data={data}
        error={error}
        tableState={tableState}
        setTableState={setTableState}
        setSelectedData={null}
        setQueryToParams={true}
      >
        <div className="space-y-6 w-full">
          <DataTableToolbar
            action={<AddNewBillingPlan />}
            showViewOptions={false}
          />
          <TableBody>
            <TableItems />
          </TableBody>
          <DataTablePagination />
        </div>
      </DataTable>
    </div>
  );
}
